"use client";

import { useRef, useState } from "react";
import { Check, RotateCcw } from "lucide-react";
import { useConfigurator } from "@/store/configurator";
import { ConfiguratorFooter } from "@/components/configurator/ConfiguratorFooter";
import { InstrumentIcon } from "@/components/configurator/InstrumentIcon";

interface Props {
  onNext: () => void;
  onBack: () => void;
  onReset: () => void;
}

export function InstrumentStep({ onNext, onReset }: Props) {
  const { state, referenceData, setInstrument } = useConfigurator();
  const canProceed = !!state.instrumentId;
  const [confirmReset, setConfirmReset] = useState(false);
  const confirmTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Reset wipes every downstream choice — ask for a second tap instead of
  // a native confirm dialog (house rule: no browser modals).
  function handleReset() {
    if (!confirmReset) {
      setConfirmReset(true);
      confirmTimer.current = setTimeout(() => setConfirmReset(false), 3000);
      return;
    }
    if (confirmTimer.current) clearTimeout(confirmTimer.current);
    setConfirmReset(false);
    onReset();
  }

  function select(id: string) {
    setInstrument(id);
  }

  return (
    <div>
      {state.instrumentId ? (
      <div className="mb-6 flex items-center justify-between gap-3">
        <button
          type="button"
          onClick={handleReset}
          className="inline-flex items-center gap-2 rounded-sm border px-3 py-2 text-xs font-semibold transition-colors hover:border-[#3A3A3A] focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#E8C547]"
          style={{
            borderColor: confirmReset ? "#E84040" : "#2A2A2A",
            color: confirmReset ? "#F5B5B5" : "#9A9A9A",
          }}
          aria-label={confirmReset ? "Tap again to reset the whole build" : "Reset configurator and return to the first step"}
        >
          <RotateCcw size={13} aria-hidden />
          {confirmReset ? "Tap again to reset" : "Reset build"}
        </button>
      </div>
      ) : null}

      <h2
        className="text-2xl font-black mb-2"
        style={{ fontFamily: "var(--font-display)" }}
      >
        What are you playing?
      </h2>
      <p className="text-sm mb-8" style={{ color: "#9A9A9A" }}>
        Select your instrument to get started.
      </p>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        {referenceData.instruments.map((instrument) => {
          const selected = state.instrumentId === instrument.slug;
          const desc =
            instrument.description ??
            instrument.shopBlurb ??
            `${instrument.stringCounts.map((count) => count.label).join(", ")}`;

          return (
            <button
              key={instrument.id}
              type="button"
              onClick={() => select(instrument.slug)}
              aria-pressed={selected}
              className="relative flex items-center gap-4 p-5 rounded-md border text-left transition-all hover:border-[#E8C547] hover:scale-[1.01] focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#E8C547]"
              style={{
                backgroundColor: selected ? "#E8C54710" : "#141414",
                borderColor: selected ? "#E8C547" : "#2A2A2A",
                boxShadow: selected ? "0 0 0 1px #E8C54755 inset" : undefined,
              }}
            >
              <span className="flex-shrink-0 text-[#E8C547]">
                <InstrumentIcon category={instrument.category} />
              </span>
              <div>
                <p className="font-bold" style={{ fontFamily: "var(--font-display)" }}>
                  {instrument.name}
                </p>
                <p className="text-xs mt-0.5" style={{ color: "#8A8A8A" }}>
                  {desc}
                </p>
              </div>
              {selected ? (
                <span
                  className="ml-auto flex h-7 w-7 items-center justify-center rounded-full"
                  style={{ backgroundColor: "#E8C547", color: "#0A0A0A" }}
                  aria-label={`${instrument.name} selected`}
                >
                  <Check size={16} aria-hidden />
                </span>
              ) : null}
            </button>
          );
        })}
      </div>

      <ConfiguratorFooter
        onNext={onNext}
        nextDisabled={!canProceed}
        nextHint="Choose an instrument to continue"
      />
    </div>
  );
}
